import { getUser } from '~src/user';
import {
  GLEAP_PERSONALIZATION_AGENT_TOOL,
  GLEAP_PERSONALIZATION_CATALOG_EVENT,
  isGleapPersonalizationCustomAction,
} from './giottoPersonalizations';

const INTERNAL_EMAIL_DOMAIN = '@onoranzefunebricloud.com';
const GLEAP_UNREAD_NOTIFICATIONS_KEY = 'gleap-widget-unread-notifications';
const GLEAP_SDK_SCRIPT_ID = 'gleap-sdk-script';
const GLEAP_TOUR_QUERY_PARAM = 'gleap_tour';
const GLEAP_TOUR_DELAY_QUERY_PARAM = 'gleap_tour_delay';
const GLEAP_TOUR_ANCHOR_QUERY_PARAM = 'gleap_tour_anchor';
const MAX_PENDING_TRACKED_EVENTS = 50;
const MAX_PRODUCT_TOUR_DELAY_SECONDS = 30;
const MAX_PRODUCT_TOUR_ANCHOR_WAIT_MS = 30_000;
const PRODUCT_TOUR_ANCHOR_POLL_MS = 100;
const PRODUCT_TOUR_ANCHOR_PATTERN = /^[a-z0-9-]{1,80}$/i;

const DEPRECATED_CONTACT_CUSTOM_DATA_KEYS = [
  // Gleap already stores the widget language on the native `lang` field.
  'user_language',
  'org_id',
  'org_name',
  'org_modulare',
  'plan',
  'ragione_sociale',
  'partita_iva',
  'tipo_licenza',
  'tipo_istanza',
  'tipo_piano',
  'stato',
  'indirizzo',
  'provincia',
  'cap',
  'citta',
  'stripe_customer_id',
  'website',
  'moduli',
  'org_modules_count',
  'org_modules_skus',
  'org_modules_names',
  'org_created_at',
  'upgraded_at',
];

let initialized = false;
let sdkReady = false;
let enabled = true;
let identifiedUserId = null;
let identifiedOrgId = null;
let identifiedPayload = null;
let contactSync = Promise.resolve();
let realtimeListenersRegistered = false;
let customActionsRegistered = false;
let missingApiKeyReported = false;
let pendingProductTour = null;
let waitingProductTourKey = null;

const scheduledProductTourKeys = new Set();
const pendingTrackedEvents = [];

const toDate = (value) => {
  if (!value) {
    return undefined;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const compactCustomData = (data) =>
  Object.entries(data).reduce((result, [key, value]) => {
    if (['string', 'number', 'boolean'].includes(typeof value)) {
      result[key] = value;
    }
    return result;
  }, {});

const buildContactCustomData = (data) => ({
  ...Object.fromEntries(
    DEPRECATED_CONTACT_CUSTOM_DATA_KEYS.map((key) => [key, null])
  ),
  ...compactCustomData(data),
});

const toGleapError = (reason) => {
  if (reason instanceof Error) {
    return reason;
  }

  return new Error(
    reason == null
      ? 'Gleap SDK rejected without an error value'
      : `Gleap SDK rejected with a non-Error ${typeof reason} value`
  );
};

// Risolve a `true` solo quando l'operazione va a buon fine: chi la invoca deve
// poter distinguere un fallimento per non considerare sincronizzato lo stato.
const runGleapOperation = (operation, callback) => {
  const reportFailure = (reason) => {
    console.error(`[Gleap] ${operation} failed`, toGleapError(reason));
    return false;
  };

  try {
    // Gleap types these methods as void, but its runtime can return a Promise.
    return Promise.resolve(callback()).then(() => true, reportFailure);
  } catch (reason) {
    reportFailure(reason);
    return Promise.resolve(false);
  }
};

const ensureGleapSdk = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (window.Gleap) {
    return window.Gleap;
  }

  window.GleapActions = window.GleapActions || [];
  window.Gleap = new Proxy(
    { invoked: true },
    {
      get: (target, action) => {
        if (action === 'invoked') {
          return target.invoked;
        }

        return (...args) => {
          window.GleapActions.push({ e: action, a: args });
        };
      },
    }
  );

  if (!document.getElementById(GLEAP_SDK_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GLEAP_SDK_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://sdk.gleap.io/latest/index.js';
    script.onerror = () => console.error('[Gleap] SDK failed to load');
    document.head.appendChild(script);
  }

  return window.Gleap;
};

export const getEternoGleapActionTarget = (actionName) => {
  const eternoUrl = process.env.ETERNO_URL;
  if (!eternoUrl || typeof actionName !== 'string' || !actionName.trim()) {
    return null;
  }

  try {
    const target = new URL(`${eternoUrl.replace(/\/$/, '')}/v2/gleap-action`);
    target.searchParams.set('name', actionName);
    return target.toString();
  } catch (error) {
    return null;
  }
};

const registerGleapCustomActions = (gleap) => {
  if (customActionsRegistered || typeof window === 'undefined') {
    return;
  }

  gleap.registerCustomAction((action) => {
    if (isGleapPersonalizationCustomAction(action?.name)) {
      if (typeof gleap.close === 'function') {
        gleap.close();
      }
      window.dispatchEvent(
        new CustomEvent(GLEAP_PERSONALIZATION_CATALOG_EVENT)
      );
      return;
    }

    const target = getEternoGleapActionTarget(action?.name);
    if (target) {
      window.location.assign(target);
    }
  });

  gleap.registerAgentTool(GLEAP_PERSONALIZATION_AGENT_TOOL, async () => {
    if (typeof gleap.close === 'function') {
      gleap.close();
    }
    window.dispatchEvent(new CustomEvent(GLEAP_PERSONALIZATION_CATALOG_EVENT));
    return 'Il catalogo delle personalizzazioni è stato aperto.';
  });

  customActionsRegistered = true;
};

const captureProductTourFromUrl = () => {
  if (typeof window === 'undefined' || pendingProductTour) {
    return;
  }

  let url;
  try {
    url = new URL(window.location.href);
  } catch (error) {
    return;
  }

  const id = url.searchParams.get(GLEAP_TOUR_QUERY_PARAM)?.trim();
  if (!id) {
    return;
  }

  const requestedDelay = Number(
    url.searchParams.get(GLEAP_TOUR_DELAY_QUERY_PARAM)
  );
  const delaySeconds = Number.isFinite(requestedDelay)
    ? Math.min(Math.max(requestedDelay, 0), MAX_PRODUCT_TOUR_DELAY_SECONDS)
    : 0;
  const key = `${url.pathname}:${id}`;
  const requestedAnchor = url.searchParams
    .get(GLEAP_TOUR_ANCHOR_QUERY_PARAM)
    ?.trim();
  const anchor =
    requestedAnchor && PRODUCT_TOUR_ANCHOR_PATTERN.test(requestedAnchor)
      ? requestedAnchor
      : undefined;

  if (!scheduledProductTourKeys.has(key)) {
    pendingProductTour = {
      anchor,
      delayMs: delaySeconds * 1000,
      id,
      key,
      readyForContact: false,
    };
  }

  // Avoid evaluating URL audiences before the authenticated contact is ready.
  url.searchParams.delete(GLEAP_TOUR_QUERY_PARAM);
  url.searchParams.delete(GLEAP_TOUR_DELAY_QUERY_PARAM);
  url.searchParams.delete(GLEAP_TOUR_ANCHOR_QUERY_PARAM);
  window.history?.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
};

const isProductTourAnchorReady = (anchor) => {
  if (!anchor) {
    return true;
  }

  return Boolean(document.querySelector(`[data-gleap-tour="${anchor}"]`));
};

const startProductTourWhenReady = (gleap, tour, waitedMs = 0) => {
  if (!enabled || pendingProductTour?.key !== tour.key) {
    waitingProductTourKey = null;
    return;
  }

  if (!isProductTourAnchorReady(tour.anchor)) {
    if (waitedMs >= MAX_PRODUCT_TOUR_ANCHOR_WAIT_MS) {
      waitingProductTourKey = null;
      console.warn('[Gleap] Product Tour non avviato: schermata non pronta', {
        anchor: tour.anchor,
        id: tour.id,
        path: window.location.pathname,
      });
      return;
    }

    window.setTimeout(
      () =>
        startProductTourWhenReady(
          gleap,
          tour,
          waitedMs + PRODUCT_TOUR_ANCHOR_POLL_MS
        ),
      PRODUCT_TOUR_ANCHOR_POLL_MS
    );
    return;
  }

  pendingProductTour = null;
  waitingProductTourKey = null;
  scheduledProductTourKeys.add(tour.key);

  window.setTimeout(() => {
    if (!enabled) {
      return;
    }

    try {
      console.info('[Gleap] Avvio Product Tour', {
        anchor: tour.anchor,
        id: tour.id,
        path: window.location.pathname,
      });
      gleap.startProductTour(tour.id);
    } catch (error) {
      scheduledProductTourKeys.delete(tour.key);
    }
  }, tour.delayMs);
};

const flushPendingProductTour = (gleap = window.Gleap) => {
  if (
    !sdkReady ||
    !pendingProductTour?.readyForContact ||
    scheduledProductTourKeys.has(pendingProductTour.key) ||
    waitingProductTourKey === pendingProductTour.key
  ) {
    return;
  }

  const tour = pendingProductTour;
  waitingProductTourKey = tour.key;
  startProductTourWhenReady(gleap, tour);
};

export const startGleapProductTourFromUrl = () => {
  if (typeof window === 'undefined' || !enabled) {
    return false;
  }

  captureProductTourFromUrl();
  if (!pendingProductTour) {
    return false;
  }

  pendingProductTour.readyForContact = true;
  flushPendingProductTour();
  return true;
};

const sendTrackedEvent = (gleap, { name, data }) => {
  try {
    gleap.trackEvent(name, data);
    return true;
  } catch (error) {
    console.error('Error tracking event in Gleap', error);
    return false;
  }
};

const flushPendingTrackedEvents = (gleap) => {
  const events = pendingTrackedEvents.splice(0);
  events.forEach((event) => sendTrackedEvent(gleap, event));
};

const updateUnreadBadge = (count) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll('.gleap-unread-badge').forEach((badge) => {
    if (count > 0) {
      badge.classList.remove('badge-light');
      badge.classList.add('badge-danger');
      badge.textContent = String(count);
      return;
    }

    badge.classList.remove('badge-danger');
    badge.classList.add('badge-light');
    badge.textContent = '';
  });
};

export const getGleapUnreadConversationCount = () => {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    const cachedNotifications = window.localStorage?.getItem(
      GLEAP_UNREAD_NOTIFICATIONS_KEY
    );
    if (!cachedNotifications) {
      return 0;
    }

    const notifications = JSON.parse(cachedNotifications);
    if (!Array.isArray(notifications)) {
      return 0;
    }

    const conversations = new Set();
    notifications.forEach((notification) => {
      const conversation = notification?.data?.conversation;
      const conversationId =
        conversation?.shareToken || conversation?.id || conversation?._id;
      if (conversationId) {
        conversations.add(conversationId);
      }
    });

    return conversations.size;
  } catch (error) {
    return 0;
  }
};

const registerRealtimeListeners = (gleap) => {
  if (realtimeListenersRegistered) {
    return;
  }

  gleap.on('unread-count-changed', (count) => {
    const unreadCount = Number.isFinite(count)
      ? Math.max(0, count)
      : getGleapUnreadConversationCount();

    updateUnreadBadge(unreadCount);
    if (unreadCount <= 0) {
      return;
    }

    // Gleap emits this event for new activity as well as unread messages.
    // Refresh an already-open messenger so remote ticket state changes appear.
    if (gleap.isOpened()) {
      gleap.openConversations();
    }
  });

  gleap.on('open', () => updateUnreadBadge(0));

  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('storage', (event) => {
      if (event.key === GLEAP_UNREAD_NOTIFICATIONS_KEY) {
        updateUnreadBadge(getGleapUnreadConversationCount());
      }
    });
  }

  updateUnreadBadge(getGleapUnreadConversationCount());
  realtimeListenersRegistered = true;
};

export const setGleapEnabled = (nextEnabled) => {
  enabled = nextEnabled;
  if (!enabled) {
    pendingTrackedEvents.length = 0;
    pendingProductTour = null;
    waitingProductTourKey = null;
    if (initialized && typeof window !== 'undefined' && window.Gleap) {
      window.Gleap.hide();
    }
  }
};

export const ensureGleapInitialized = () => {
  if (typeof window === 'undefined' || !enabled) {
    return null;
  }

  const apiKey = process.env.GLEAP_API_KEY;
  if (!apiKey) {
    if (!missingApiKeyReported) {
      console.error('[Gleap] Missing GLEAP_API_KEY configuration');
      missingApiKeyReported = true;
    }
    return null;
  }

  const gleap = ensureGleapSdk();
  if (!gleap) {
    return null;
  }

  if (!initialized) {
    captureProductTourFromUrl();
    const language = document.documentElement.lang?.split('-')[0] || 'it';
    const environment = process.env.ENVIRONMENT === 'prod' ? 'prod' : 'dev';

    gleap.setUseCookies(true);
    gleap.setEnvironment(environment);
    gleap.setLanguage(language);
    gleap.on('initialized', () => {
      sdkReady = true;
      // `gleap` può essere il proxy che accoda in GleapActions: una volta
      // caricato l'SDK va usato window.Gleap, altrimenti eventi e product tour
      // in coda tornerebbero in coda invece di essere inviati.
      const sdk = window.Gleap || gleap;
      flushPendingTrackedEvents(sdk);
      flushPendingProductTour(sdk);
    });
    gleap.initialize(apiKey);
    registerGleapCustomActions(gleap);
    initialized = true;
  }

  gleap.showFeedbackButton(false);
  return gleap;
};

const buildContactProperties = (user) => {
  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const phone =
    typeof user.telefono === 'string'
      ? user.telefono.trim() || undefined
      : undefined;

  return {
    name: fullName || undefined,
    email: user.username || user.email || undefined,
    phone,
    avatar: user.profile_picture?.url,
    // Organization properties live on the Gleap company, not on contacts.
    plan: null,
    customData: buildContactCustomData({
      user_role: user.ruolo,
    }),
  };
};

export const initGleap = async () => {
  try {
    let startupResponse = await getUser();
    let user = startupResponse?.user;
    if (!user) {
      return false;
    }

    const email = user.username || user.email || '';
    if (email.toLowerCase().includes(INTERNAL_EMAIL_DOMAIN)) {
      setGleapEnabled(false);
      return false;
    }

    // Cached startup responses created before identity verification do not
    // contain the hash. Refresh once so Gleap never identifies a user with
    // client-controlled data only.
    if (!startupResponse.gleapUserHash) {
      startupResponse = await getUser({}, true);
      user = startupResponse?.user;
    }

    const gleapUserHash = startupResponse?.gleapUserHash;
    if (!user?.objectId || !gleapUserHash) {
      setGleapEnabled(false);
      console.error('[Gleap] Missing verified user identity');
      return false;
    }

    setGleapEnabled(true);

    const gleap = ensureGleapInitialized();
    if (!gleap) {
      return false;
    }

    registerRealtimeListeners(gleap);

    const org = user.org_pointer || {};
    const orgId = org.objectId || null;
    const companyName = org.nome || org.ragione_sociale;
    const contactProperties = buildContactProperties(user);
    const contactData = {
      ...contactProperties,
      company: orgId
        ? { id: orgId, name: companyName || undefined }
        : undefined,
    };
    const payloadIdentity = JSON.stringify([user.objectId, orgId, contactData]);
    const contactPayloadChanged = !(
      identifiedUserId === user.objectId &&
      identifiedOrgId === orgId &&
      identifiedPayload === payloadIdentity
    );
    const identityChanged =
      identifiedUserId !== user.objectId || identifiedOrgId !== orgId;
    const clearPreviousIdentity = Boolean(identifiedUserId);

    if (contactPayloadChanged) {
      identifiedUserId = user.objectId;
      identifiedOrgId = orgId;
      identifiedPayload = payloadIdentity;

      contactSync = contactSync
        .catch(() => undefined)
        .then(() => {
          if (identityChanged) {
            if (clearPreviousIdentity) {
              gleap.clearIdentity();
            }

            return runGleapOperation('identify contact', () =>
              gleap.identify(
                String(user.objectId),
                {
                  ...contactData,
                  createdAt: toDate(user.createdAt),
                },
                gleapUserHash
              )
            );
          }

          // identify() owns the company relationship. Sending it through
          // updateContact() can create duplicate company contacts in Gleap.
          return runGleapOperation('update contact', () =>
            gleap.updateContact(contactProperties)
          );
        })
        .then((succeeded) => {
          // Se la sincronizzazione fallisce svuotiamo la cache: senza questo il
          // prossimo initGleap() vedrebbe il payload invariato, salterebbe
          // l'identificazione e la sessione resterebbe anonima.
          if (succeeded === false && identifiedPayload === payloadIdentity) {
            identifiedUserId = null;
            identifiedOrgId = null;
            identifiedPayload = null;
          }
        });
    }

    contactSync
      .catch(() => undefined)
      .then(() => {
        if (identifiedPayload === payloadIdentity) {
          startGleapProductTourFromUrl();
        }
      });

    return true;
  } catch (error) {
    console.error('[Gleap] init failed', error);
    return false;
  }
};

export const trackGleapEvent = (eventName, eventData = {}) => {
  const gleap = ensureGleapInitialized();
  if (!gleap) {
    return false;
  }

  const event = { name: eventName, data: eventData };
  if (!sdkReady) {
    if (pendingTrackedEvents.length >= MAX_PENDING_TRACKED_EVENTS) {
      pendingTrackedEvents.shift();
    }
    pendingTrackedEvents.push(event);
    return true;
  }

  return sendTrackedEvent(gleap, event);
};

export const openGleap = ({ message, customData = {} } = {}) => {
  const gleap = ensureGleapInitialized();
  if (!gleap) {
    return false;
  }

  gleap.clearCustomData();

  const requestData = {
    app: 'giotto',
    ...(message ? { support_request: message } : {}),
    ...customData,
  };

  if (message) {
    gleap.preFillForm({ description: message });
  }

  gleap.attachCustomData(requestData);
  trackGleapEvent('support_request_opened', requestData);
  if (message) {
    gleap.startConversation();
  } else {
    gleap.open();
  }
  return true;
};

export const openGleapForm = ({
  formId,
  formData = {},
  customData = {},
} = {}) => {
  if (typeof formId !== 'string' || !formId.trim()) {
    return false;
  }

  const gleap = ensureGleapInitialized();
  if (!gleap) {
    return false;
  }

  const requestData = {
    app: 'giotto',
    ...customData,
  };

  gleap.clearCustomData();
  gleap.preFillForm(formData);
  gleap.attachCustomData(requestData);
  trackGleapEvent('personalization_request_opened', requestData);
  gleap.startClassicForm(formId);
  return true;
};

export const searchGleapHelpCenter = (term) => {
  const gleap = ensureGleapInitialized();
  if (!gleap) {
    return false;
  }

  if (term) {
    gleap.searchHelpCenter(term, false);
  } else {
    gleap.openHelpCenter(false);
  }

  return true;
};

export const resetGleap = () => {
  if (initialized && typeof window !== 'undefined' && window.Gleap) {
    window.Gleap.clearIdentity();
    window.Gleap.hide();
  }
  identifiedUserId = null;
  identifiedOrgId = null;
  identifiedPayload = null;
  contactSync = Promise.resolve();
  pendingTrackedEvents.length = 0;
  pendingProductTour = null;
  waitingProductTourKey = null;
  updateUnreadBadge(0);
};
